import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Card, Btn } from '../ui/primitives';
import {
  fetchConnectStatus,
  createOnboardingLink,
  fetchOAuthUrl,
  completeOAuth,
  createDashboardLink,
} from '../services/connectApi';
import { fetchRevenueSplit } from '../services/platformApi';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; }`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Mark = styled.div`
  height: 38px;
  flex: none;
  padding: 0 15px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(140deg, #635bff 0%, #4b45c6 100%);
  box-shadow: 0 4px 12px -3px rgba(99, 91, 255, 0.5);
  svg { height: 15px; width: auto; display: block; }
`;

/* Official Stripe wordmark */
const StripeLogo = () => (
  <svg viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Stripe">
    <path fill="#fff" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.02-13.17 4.02-.86v3.65h3.12v3.51h-3.12v5.4zm-4.91.71c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-.99 0-1.94-6.7-1.11-6.7-6.5 0-2.9 2.2-4.62 5.5-4.62 1.32 0 2.63.2 3.95.73v3.88a9.15 9.15 0 0 0-3.96-1.03c-.87 0-1.4.25-1.4.9 0 1.85 6.7 1.11 6.7 6.55z"/>
  </svg>
);

const ShareBand = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: ${({ theme }) => theme.rMd};
  background: rgba(99, 91, 255, 0.07);
  border: 1px solid rgba(99, 91, 255, 0.16);

  b {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #4b45c6;
    line-height: 1;
  }
  span {
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 760;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.text};
`;

const Sub = styled.p`
  margin: 2px 0 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
`;

const TONES = {
  ok: { fg: 'oklch(0.55 0.14 158)', bg: 'rgba(34,178,110,.12)', dot: 'oklch(0.62 0.15 158)' },
  warn: { fg: 'oklch(0.52 0.13 66)', bg: 'rgba(230,150,20,.14)', dot: 'oklch(0.72 0.16 66)' },
  idle: { fg: ({ theme }) => theme.textSubtle, bg: 'rgba(130,130,150,.12)', dot: 'oklch(0.7 0.02 260)' },
};

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: none;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $t, theme }) => (typeof TONES[$t].fg === 'function' ? TONES[$t].fg({ theme }) : TONES[$t].fg)};
  background: ${({ $t }) => TONES[$t].bg};
  &::before {
    content: '';
    width: 7px; height: 7px; border-radius: 50%;
    background: ${({ $t }) => TONES[$t].dot};
    box-shadow: 0 0 0 3px ${({ $t }) => TONES[$t].bg};
  }
`;

const Body = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.border};
  animation: ${fadeIn} 0.3s ease both;
`;

const Text = styled.p`
  margin: 0 0 14px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.55;
  max-width: 62ch;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const AcctChip = styled.code`
  font-size: 12px;
  padding: 3px 9px;
  border-radius: 7px;
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textMuted};
`;

const Reqs = styled.div`
  margin: 2px 0 14px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
`;

const Err = styled.p`
  margin: 12px 0 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: oklch(0.55 0.16 25);
`;

/**
 * Payouts — connect a Stripe account (new Express, or an existing account via OAuth)
 * so the instructor receives their share of course sales.
 */
export default function PayoutSetup() {
  const userId = localStorage.getItem('userId');
  const [status, setStatus] = useState(null);
  const [split, setSplit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    try {
      setStatus(await fetchConnectStatus(userId));
    } catch (e) {
      setError('Could not load your payout status.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      if (code && state && state === userId) {
        try { await completeOAuth(userId, code); }
        catch (e) { setError('Could not link your Stripe account. Please try again.'); }
        window.history.replaceState({}, '', window.location.pathname);
      }
      await refresh();
      try { setSplit(await fetchRevenueSplit()); } catch (e) { /* non-blocking */ }
    })();
  }, [userId, refresh]);

  const goExpress = async () => {
    setBusy(true); setError(null);
    try { const { url } = await createOnboardingLink(userId); window.location.href = url; }
    catch (e) { setError(e.message); setBusy(false); }
  };
  const goOAuth = async () => {
    setBusy(true); setError(null);
    try { const { url } = await fetchOAuthUrl(userId); window.location.href = url; }
    catch (e) { setError(e.message || 'Connecting an existing account is not available yet.'); setBusy(false); }
  };
  const openDashboard = async () => {
    try { const { url } = await createDashboardLink(userId); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e.message); }
  };

  const connected = status?.connected;
  const enabled = status?.payoutsEnabled;
  const dueCount = status?.requirementsDue?.length || 0;

  const tone = !connected ? 'idle' : enabled ? 'ok' : 'warn';
  const pillLabel = loading ? 'Checking…' : !connected ? 'Not connected' : enabled ? 'Payouts enabled' : 'Action needed';

  return (
    <Card $pad style={{ marginBottom: 16 }}>
      <Head>
        <Brand>
          <Mark><StripeLogo /></Mark>
          <div>
            <Title>Payouts</Title>
            <Sub>Get paid for your course sales, straight to your bank.</Sub>
          </div>
        </Brand>
        <Pill $t={tone}>{pillLabel}</Pill>
      </Head>

      <Body>
        {split && (
          <ShareBand>
            <b>You keep {split.instructorSharePercent}%</b>
            <span>of every course sale · {split.platformFeePercent}% platform fee</span>
          </ShareBand>
        )}
        {loading ? (
          <Text style={{ margin: 0, opacity: 0.6 }}>Checking your payout account…</Text>
        ) : !connected ? (
          <>
            <Text>
              Connect a Stripe account to receive your earnings. Set up a new account in a minute, or
              link one you already have — Stripe handles identity verification and bank payouts.
            </Text>
            <Row>
              <Btn $variant="primary" disabled={busy} onClick={goExpress}>
                {busy ? 'Opening…' : 'Set up payouts'}
              </Btn>
              <Btn $variant="ghost" disabled={busy} onClick={goOAuth}>
                Connect existing account
              </Btn>
            </Row>
          </>
        ) : !enabled ? (
          <>
            <Text style={{ marginBottom: 8 }}>
              Almost there — Stripe still needs a few details before it can pay you.
            </Text>
            {dueCount > 0 && (
              <Reqs>{dueCount} item{dueCount === 1 ? '' : 's'} outstanding on your Stripe account.</Reqs>
            )}
            <Btn $variant="primary" disabled={busy} onClick={goExpress}>
              {busy ? 'Opening…' : 'Finish Stripe setup'}
            </Btn>
            {status.accountType === 'standard' && (
              <Text style={{ margin: '10px 0 0' }}>
                Using an existing account? You can also finish verification in your own Stripe dashboard.
              </Text>
            )}
          </>
        ) : (
          <>
            <Row style={{ marginBottom: status.dashboardAvailable ? 14 : 0 }}>
              <Text style={{ margin: 0 }}>
                You're all set — earnings from your sales pay out automatically.
              </Text>
              <AcctChip>{status.accountType === 'express' ? 'Express' : 'Standard'} · {status.accountId}</AcctChip>
            </Row>
            {status.dashboardAvailable && (
              <Btn $variant="ghost" onClick={openDashboard}>Open Stripe dashboard →</Btn>
            )}
          </>
        )}

        {error && <Err>{error}</Err>}
      </Body>
    </Card>
  );
}
